import { Drawer, Stack, Text, Group, Divider, ThemeIcon, Box } from '@mantine/core'
import { IconThumbUp, IconThumbDown, IconMessage } from '@tabler/icons-react'
import { Review, Notes } from '../../../../types'
import { reviewNotes } from '../../../../utils/review'
import Note from '../../../../components/Note'
import dayjs from 'dayjs'

type ReviewDrawerProps = {
  review: Review
  opened: boolean
  onClose: () => void
}

function BulletList({ text }: { text: string }) {
  const items = text.split(/\n|,/).map((s) => s.trim()).filter(Boolean)
  if (items.length === 1) return <Text size="sm">{items[0]}</Text>
  return (
    <Stack gap={4}>
      {items.map((item, i) => (
        <Group key={i} gap="xs" align="flex-start">
          <Text size="sm" c="dimmed" mt={1}>•</Text>
          <Text size="sm" style={{ flex: 1 }}>{item}</Text>
        </Group>
      ))}
    </Stack>
  )
}

export function ReviewDrawer({ review, opened, onClose }: ReviewDrawerProps) {
  const vehicle = review.vehicle_version

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Détail de l'avis"
      position="right"
      size="md"
    >
      <Stack gap="lg">

        {/* Véhicule + note globale */}
        <Group justify="space-between" align="flex-start">
          <Box>
            {vehicle && (
              <>
                <Text fw={600} size="md">
                  {vehicle.brand?.title} {vehicle.modele}
                </Text>
                <Text size="sm" c="dimmed">{vehicle.sra_commercial}</Text>
              </>
            )}
            <Text size="xs" c="dimmed" mt={4}>
              {dayjs(review.created_at).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Box>
          <Note note={review.note} size="medium" />
        </Group>

        {/* Tops */}
        {review.tops && (
          <>
            <Divider />
            <Stack gap="xs">
              <Group gap="xs">
                <ThemeIcon color="teal" variant="light" size="sm">
                  <IconThumbUp size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="teal.7">Points positifs</Text>
              </Group>
              <BulletList text={review.tops} />
            </Stack>
          </>
        )}

        {/* Flops */}
        {review.flops && (
          <>
            <Divider />
            <Stack gap="xs">
              <Group gap="xs">
                <ThemeIcon color="red" variant="light" size="sm">
                  <IconThumbDown size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="red.7">Points négatifs</Text>
              </Group>
              <BulletList text={review.flops} />
            </Stack>
          </>
        )}

        {/* Commentaire */}
        {review.comment && (
          <>
            <Divider />
            <Stack gap="xs">
              <Group gap="xs">
                <ThemeIcon color="blue" variant="light" size="sm">
                  <IconMessage size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="blue.7">Commentaire</Text>
              </Group>
              <Text size="sm">{review.comment}</Text>
            </Stack>
          </>
        )}

        {/* Notes par critère */}
        <Divider />
        <Stack gap="xs">
          <Text size="sm" fw={600} c="dimmed">Notes par critère</Text>
          {reviewNotes.map((note) => {
            const value = (review as Review & Notes)[note.name as keyof Notes]
            if (!value) return null
            return (
              <Group key={note.name} justify="space-between">
                <Text size="sm">{note.label}</Text>
                <Note note={value} size="small" />
              </Group>
            )
          })}
        </Stack>

      </Stack>
    </Drawer>
  )
}
